/** Private immutable research capture. Public Reports expose only the summary. */
export type ManifestJson = null | boolean | number | string | ManifestJson[] | {
    [key: string]: ManifestJson;
};
export type ManifestSectionStatus = 'captured' | 'unavailable' | 'not-applicable';
export interface ManifestSection {
    status: ManifestSectionStatus;
    reason: string | null;
    records: ManifestJson;
}
export interface ResearchManifestAttempt {
    ordinal: number;
    provider: string;
    requestedModel: string | null;
    resolvedModel: string | null;
    providerReportedModel?: string | null;
    status: 'succeeded' | 'failed';
    reasonCode: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    requestDigest: string;
    responseDigest: string | null;
    promptStatus: 'exact' | 'unavailable';
    promptReason: string | null;
    systemPrompt: string | null;
    userPrompt: string | null;
    settings: {
        temperature?: number;
        maxTokens?: number;
        seed?: number;
        topP?: number;
    };
    finishReason: string | null;
    usage: {
        promptTokens?: number;
        completionTokens?: number;
        totalTokens?: number;
    } | null;
    promptVersion: string | null;
    codeVersion: string | null;
    configVersion: string | null;
    sdkTransportRetries: null;
    sdkTransportRetryReason: 'unobservable-sdk-internal-retries';
}
export interface ResearchManifestCapture {
    version: 'tsm-research-manifest-capture-v1';
    kind: 'trader' | 'institutional' | 'who';
    capturedAt: string;
    identity: {
        symbol: string;
        researchType: 'digital-asset-trader' | 'tradfi' | 'digital-assets' | 'intelligence-evidence';
        jobId: string | null;
    };
    evidence: ManifestSection;
    calculations: ManifestSection;
    execution: {
        status: ManifestSectionStatus;
        reason: string | null;
        requestedProvider: string | null;
        requestedModel: string | null;
        resolvedProvider: string | null;
        resolvedModel: string | null;
        promptMode: string | null;
        attempts: ResearchManifestAttempt[];
    };
    output: {
        status: ManifestSectionStatus;
        reason: string | null;
        structured: ManifestJson;
    };
    versions: {
        capture: string;
        code: string | null;
        config: string | null;
        calculator: string | null;
        prompt: string | null;
    };
    /** SHA256 binds these exact Python-produced UTF8 bytes; it is not a cross-runtime canonical JSON claim. */
    producer?: {
        version: 'ttma-research-capture-v1';
        serialized: string;
        digest: string;
    };
}
export interface ResearchManifest {
    version: 'tsm-research-manifest-v1';
    reportId: string;
    archivedAt: string;
    capture: ResearchManifestCapture;
    sectionDigests: {
        evidence: string;
        calculations: string;
        execution: string;
        output: string;
        versions: string;
    };
    referenceMap: {
        status: 'captured' | 'unavailable';
        reason: string | null;
        links: Array<{
            evidenceId: string;
            claimId: string | null;
            relation: 'context';
            reason: string;
        }>;
    };
    directionalRelations: {
        status: 'unavailable';
        reason: 'support-oppose-map-not-produced';
    };
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
export type ResearchManifestStatus = 'captured' | 'metadata-incomplete' | 'disabled' | 'integrity-failed' | 'unsupported-schema';
export interface ResearchManifestSummary {
    status: ResearchManifestStatus;
    version: string | null;
    digest: string | null;
    reasons: string[];
}
export interface ResearchManifestRead extends ResearchManifestSummary {
    manifest: ResearchManifest | null;
}
export interface ResearchManifestReplay {
    status: 'matched' | 'mismatch' | 'unsupported';
    reasons: string[];
    calculations: ManifestJson;
}

/** R10 uses a new payload version. R09 interfaces and historical hashes stay exact. */
export interface ResearchDossierManifestV2 {
    version: 'tsm-research-manifest-v2';
    reportId: string;
    archivedAt: string;
    capture: import('./researchDossier').ResearchDossierCaptureV2;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'admission' | 'result' | 'calculations' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** R11 has a dedicated strict envelope; earlier capture bytes are never upgraded. */
export interface ExposureReactionDossierManifestV3 {
    version: 'tsm-research-manifest-v3';
    reportId: string;
    archivedAt: string;
    capture: import('./exposureReactionDossier').ExposureReactionDossierCaptureV3;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'result' | 'calculations' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E03 freezes one retained event review; prior manifest envelopes stay exact. */
export interface EventEvidenceReviewManifestV4 {
    version: 'tsm-research-manifest-v4';
    reportId: string;
    archivedAt: string;
    capture: import('./eventEvidenceReview').EventEvidenceReviewCaptureV4;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E04 keeps original retained evidence and adds frozen authoritative history. */
export interface EventEvidenceReviewManifestV5 {
    version: 'tsm-research-manifest-v5';
    reportId: string;
    archivedAt: string;
    capture: import('./authoritativeEventHistory').EventEvidenceReviewCaptureV5;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'history' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E05 freezes exact USGS origin-product history without changing earlier envelopes. */
export interface EventEvidenceReviewManifestV6 {
    version: 'tsm-research-manifest-v6';
    reportId: string;
    archivedAt: string;
    capture: import('./usgsProductHistory').EventEvidenceReviewCaptureV6;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'productHistory' | 'productHistoryResult' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E06 retains the complete v6 parent and a separate local ShakeMap interpretation. */
export interface EventEvidenceReviewManifestV7 {
    version: 'tsm-research-manifest-v7';
    reportId: string;
    archivedAt: string;
    capture: import('./usgsShakeMapSummary').EventEvidenceReviewCaptureV7;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions' | 'originReview' | 'shakeMapSummary' | 'shakeMapSummaryResult', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E07 freezes the unchanged E03 root with one retained PAGER interpretation. */
export interface EventEvidenceReviewManifestV8 {
    version: 'tsm-research-manifest-v8';
    reportId: string;
    archivedAt: string;
    capture: import('./usgsPagerSummary').EventEvidenceReviewCaptureV8;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'eventReview' | 'pagerSummary' | 'pagerSummaryResult' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E08 freezes the unchanged E03 root with one dated facility-point screen. */
export interface EventEvidenceReviewManifestV9 {
    version: 'tsm-research-manifest-v9';
    reportId: string;
    archivedAt: string;
    capture: import('./facilityExposure').EventEvidenceReviewCaptureV9;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions' | 'eventReview' | 'facilityExposure' | 'facilityExposureResult', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
/** E09 retains exact PAGER bodies beside the unchanged E03 root. */
export interface EventEvidenceReviewManifestV10 {
    version: 'tsm-research-manifest-v10';
    reportId: string;
    archivedAt: string;
    capture: import('./usgsPagerSourceAudit').EventEvidenceReviewCaptureV10;
    sectionDigests: Record<'definition' | 'question' | 'evidence' | 'result' | 'parentOperands' | 'changeExplanation' | 'execution' | 'versions' | 'eventReview' | 'pagerSourceAudit' | 'pagerSourceAuditResult', string>;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
export type AnyEventEvidenceReviewManifest = EventEvidenceReviewManifestV4 | EventEvidenceReviewManifestV5 | EventEvidenceReviewManifestV6 | EventEvidenceReviewManifestV7 | EventEvidenceReviewManifestV8 | EventEvidenceReviewManifestV9 | EventEvidenceReviewManifestV10;
/** Availability follows verified committed readback. This does not certify publication time. */
export interface EventEvidenceReviewRetentionReceipt {
    reportId: string;
    manifestDigest: string;
    acknowledgedAt: string;
    digest: string;
}
export type AnyResearchManifest = ResearchManifest | ResearchDossierManifestV2 | ExposureReactionDossierManifestV3 | AnyEventEvidenceReviewManifest | IdeasResearchManifestV11;
export interface AnyResearchManifestRead extends ResearchManifestSummary {
    manifest: AnyResearchManifest | null;
}

export function canonicalManifestJson(value: unknown): string {
    if (Array.isArray(value))
        return `[${value.map(canonicalManifestJson).join(',')}]`;
    if (value && typeof value === 'object')
        return `{${Object.keys(value).sort().map(k => `${JSON.stringify(k)}:${canonicalManifestJson((value as Record<string, unknown>)[k])}`).join(',')}}`;
    return JSON.stringify(value) ?? 'null';
}

/** E13 stores one exact historical Ideas projection. */
export interface IdeasRetentionCaptureV1 {
    version: 'tsm-ideas-retention-capture-v1';
    researchType: 'ideas';
    generatedAt: string;
    assessedAt: string;
    proof: { id: string; issuedAt: string; expiresAt: string };
    responseDigest: string;
    retainedResultDigest: string;
    contentCoverage: 'complete' | 'metadata-only';
    retainedResponse: import('./ideasRetention').IdeasRetainedResponseV1;
    notRetained: import('./ideasRetention').IdeasNotRetainedV1[];
    dependencies: import('./ideasRetention').IdeasRetentionDependencyV1[];
    provenance: import('./ideasRetention').IdeasRetentionProvenanceV1;
    rendererVersion: 'tsm-ideas-retention-markdown-v1';
    markdownDigest: string;
}
export interface IdeasResearchManifestV11 {
    version: 'tsm-research-manifest-v11';
    reportId: string;
    archivedAt: string;
    capture: IdeasRetentionCaptureV1;
    captureDigest: string;
    markdownDigest: string;
    digest: string;
}
